/**
 * Shiny Hunt (2026-09-03 owner ask; always-on since 2026-09-08): what is
 * boosting shiny odds RIGHT NOW in the trainer's local time, what shiny is
 * NEW, and which species each of those covers.
 *
 * WHAT COUNTS AS BOOSTED. An event boosts shiny odds when its bonus lines say
 * so ("Increased chance of Shiny …"), or when it is one of the kinds GO
 * always boosts for its featured Pokémon: Community Day, Raid Day, Hatch Day
 * and GO Fest. A Spotlight Hour does not (its shinies are the ordinary
 * rate), so a `canBeShiny` spawn alone never qualifies an event.
 *
 * THE PAGE'S OWN SHINY SECTION (2026-09-08). The worker reads each event's
 * LeekDuck page and delivers `shinyFeatures`: "Shiny Debut" species (kind
 * debut) and "Increased Shiny Chance" species (kind boost), the latter often
 * on a WINDOW inside the event ("Shiny Pidgey from September 8 at 10:00
 * a.m. to September 11 at 10:00 a.m."). A boost window is its own hunt entry
 * timed by that window; a debut is a new shiny to chase from the moment it
 * is announced until a week after the event ends.
 *
 * WHEN IT SHOWS. A boost from its start until twelve hours after its end, so
 * a trainer who played the afternoon can still tick what they caught that
 * evening. Instants come from the shared recency helpers, which read GO's
 * floating wall-clock the way the Calendar does.
 *
 * Pure: the Profile block feeds it the live feed and the clock.
 */
import { eventCountdownMs } from "./calendarLinks";
import { eventEndMs, eventStartMs } from "./eventRecency";
import { habitatScheduleFor, scheduleLiveFromMs } from "./goFestHabitats";

export const SHINY_HUNT_LINGER_MS = 12 * 60 * 60 * 1000;
/**
 * A fest with a habitat schedule may show a PREVIEW this far before it
 * starts: the weekend's habitats on the Friday, marked as a preview, going
 * live at 10 a.m. local (owner direction 2026-09-04, evening: switched on
 * ahead of the Mega Finale). 0 hides the block until the fest opens.
 */
export const SHINY_HUNT_PREVIEW_MS = 48 * 60 * 60 * 1000;
/** A stated boost window shows as "coming" this far ahead (the dates are news). */
export const SHINY_BOOST_PREVIEW_MS = 7 * 24 * 60 * 60 * 1000;
/** A shiny debut shows from this far before its event ("announced") ... */
export const SHINY_DEBUT_PREVIEW_MS = 21 * 24 * 60 * 60 * 1000;
/** ... until this long after the event ends (still new, ordinary odds). */
export const SHINY_DEBUT_LINGER_MS = 7 * 24 * 60 * 60 * 1000;

const BOOSTING_CATEGORIES: ReadonlySet<string> = new Set([
  "community-day",
  "raid-day",
  "hatch-day",
  "go-fest",
]);

export interface ShinyHuntSpawn {
  speciesId?: number;
  name?: string;
  shinyBoost?: boolean;
  variants?: readonly string[];
}

/** One row of the page's Shiny section, as the worker delivers it. */
export interface ShinyFeatureInput {
  speciesId: number;
  name?: string;
  /** Regional-form suffix (alola / galar / hisui / paldea). */
  formSuffix?: string;
  kind: "debut" | "boost";
  /** Wall-clock local window, when the page states one. */
  from?: string;
  to?: string;
}

export interface ShinyHuntEventInput {
  id: string;
  name: string;
  category: string;
  start?: string | null;
  end?: string | null;
  spawns?: readonly ShinyHuntSpawn[];
  bonuses?: readonly string[];
  shinyFeatures?: readonly ShinyFeatureInput[];
}

/** A species (optionally a regional form of it) to chase in Shiny. */
export interface ShinyTarget {
  speciesId: number;
  formSuffix?: string;
}

export interface ShinyHuntEvent<E extends ShinyHuntEventInput = ShinyHuntEventInput> {
  event: E;
  /** Species with boosted shiny odds, dex order of appearance in the feed. */
  speciesIds: number[];
  /** The same species with their form suffixes; the tiles draw these. */
  targets: ShinyTarget[];
  /** True while the boost is still running; false in the twelve-hour linger. */
  live: boolean;
  /** True before the boost starts: a preview of a scheduled fest or a stated window. */
  preview: boolean;
  /**
   * Set when this entry is ONE stated boost window inside the event (Mega
   * Squads: Pidgey Sep 8 to 11, then Carvanha Sep 11 to 14) rather than the
   * event as a whole. Local instants, ms.
   */
  window?: { start: number; end: number };
}

export interface ShinyDebut<E extends ShinyHuntEventInput = ShinyHuntEventInput> {
  event: E;
  targets: ShinyTarget[];
  /** The event is running: the new shiny is out. */
  live: boolean;
  /** Before the event starts: announced, not yet out. */
  upcoming: boolean;
  /** Local instant the debut happens (the event's start), ms. */
  startMs: number;
  /** Local instant the event ends, ms. */
  endMs: number;
}

export function eventBoostsShiny(ev: ShinyHuntEventInput): boolean {
  if (BOOSTING_CATEGORIES.has(ev.category)) return true;
  return (ev.bonuses ?? []).some((line) => /\bshiny\b/i.test(line));
}

/** The species an event boosts: a flagged shiny spawn, or an admin Shiny link. */
export function shinySpeciesFor(ev: ShinyHuntEventInput): number[] {
  const out: number[] = [];
  for (const s of ev.spawns ?? []) {
    const id = s.speciesId;
    if (typeof id !== "number" || !Number.isFinite(id) || id < 1) continue;
    const boosted = s.variants ? s.variants.includes("shiny") : s.shinyBoost !== false;
    if (!boosted || out.includes(id)) continue;
    out.push(id);
  }
  return out;
}

function validFeature(f: ShinyFeatureInput): boolean {
  return typeof f.speciesId === "number" && Number.isFinite(f.speciesId) && f.speciesId >= 1;
}

function targetKey(t: ShinyTarget): string {
  return `${t.speciesId}:${t.formSuffix ?? ""}`;
}

function dedupeTargets(list: readonly ShinyTarget[]): ShinyTarget[] {
  const seen = new Set<string>();
  const out: ShinyTarget[] = [];
  for (const t of list) {
    const k = targetKey(t);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push({ speciesId: t.speciesId, ...(t.formSuffix ? { formSuffix: t.formSuffix } : {}) });
  }
  return out;
}

/** Local instant of a wall-clock window string, NaN when unreadable. */
function windowMs(value: string | undefined): number {
  if (!value) return NaN;
  const ms = eventCountdownMs(value);
  return Number.isFinite(ms) ? ms : NaN;
}

/**
 * The page's stated boost windows, one hunt entry per distinct window (a
 * window with no dates takes the event's own span). Species the event
 * already boosts as a whole (`already`) are left out so a Community Day's
 * featured Pokémon is never listed twice.
 */
function boostWindowEntries<E extends ShinyHuntEventInput>(
  ev: E,
  already: ReadonlySet<number>,
  now: number,
): ShinyHuntEvent<E>[] {
  const boosts = (ev.shinyFeatures ?? []).filter((f) => f.kind === "boost" && validFeature(f));
  if (boosts.length === 0) return [];
  const evStart = eventStartMs(ev);
  const evEnd = eventEndMs(ev);
  const groups = new Map<string, { start: number; end: number; stated: boolean; targets: ShinyTarget[] }>();
  for (const f of boosts) {
    if (already.has(f.speciesId)) continue;
    let start = windowMs(f.from);
    let end = windowMs(f.to);
    const stated = Number.isFinite(start) && Number.isFinite(end) && end > start;
    if (!stated) {
      start = evStart;
      end = evEnd;
    }
    if (!Number.isFinite(end)) continue;
    const key = `${start}:${end}`;
    const g = groups.get(key) ?? { start, end, stated, targets: [] };
    g.targets.push({ speciesId: f.speciesId, ...(f.formSuffix ? { formSuffix: f.formSuffix } : {}) });
    groups.set(key, g);
  }
  const out: ShinyHuntEvent<E>[] = [];
  for (const g of groups.values()) {
    if (now >= g.end + SHINY_HUNT_LINGER_MS) continue;
    if (now < g.start && g.start - now > SHINY_BOOST_PREVIEW_MS) continue;
    const targets = dedupeTargets(g.targets);
    if (targets.length === 0) continue;
    out.push({
      event: ev,
      speciesIds: targets.map((t) => t.speciesId),
      targets,
      live: now >= g.start && now < g.end,
      preview: now < g.start,
      ...(g.stated ? { window: { start: g.start, end: g.end } } : {}),
    });
  }
  return out;
}

/** Events to show in the Shiny Hunt block at `now`, live ones first. */
export function shinyHuntEvents<E extends ShinyHuntEventInput>(
  events: readonly E[],
  now: number = Date.now(),
): ShinyHuntEvent<E>[] {
  const out: ShinyHuntEvent<E>[] = [];
  const seen = new Set<string>();
  for (const ev of events) {
    if (seen.has(ev.id)) continue;
    seen.add(ev.id);
    const wholeEvent = new Set<number>();
    if (eventBoostsShiny(ev)) {
      const end = eventEndMs(ev);
      const schedule = habitatScheduleFor(ev.id);
      const scheduled = schedule != null;
      // A scheduled fest may go live before the feed's start (liveFromHour:
      // the Mega Finale reads live from midnight of its first day).
      const start = Math.min(eventStartMs(ev), schedule ? scheduleLiveFromMs(schedule) : Infinity);
      const inWindow =
        Number.isFinite(end) &&
        now < end + SHINY_HUNT_LINGER_MS &&
        // Before the start: only a habitat-scheduled fest, and only within the
        // preview window (2026-09-04 owner ask: see the weekend's habitats on
        // Friday), as a preview.
        (now >= start || (scheduled && SHINY_HUNT_PREVIEW_MS > 0 && start - now <= SHINY_HUNT_PREVIEW_MS));
      if (inWindow) {
        const speciesIds = shinySpeciesFor(ev);
        // A fest with a curated habitat schedule boosts its habitats' spawns
        // whatever the feed managed to list (2026-09-04): it qualifies on the
        // schedule alone, and the block draws the habitats.
        if (speciesIds.length > 0 || scheduled) {
          for (const id of speciesIds) wholeEvent.add(id);
          out.push({
            event: ev,
            speciesIds,
            targets: speciesIds.map((speciesId) => ({ speciesId })),
            live: now >= start && now < end,
            preview: now < start,
          });
        }
      }
    }
    out.push(...boostWindowEntries(ev, wholeEvent, now));
  }
  // Live first; among the rest the soonest start first (a preview of the
  // next window sits above one that has ended).
  const startOf = (h: ShinyHuntEvent<E>) => h.window?.start ?? eventStartMs(h.event);
  out.sort((a, b) => Number(b.live) - Number(a.live) || startOf(a) - startOf(b));
  return out;
}

/**
 * New shinies: the debuts the feed's pages announce, from three weeks before
 * their event to a week after it ends. Live debuts first (the shortest
 * event first: the debut's own event over the season page that also names
 * it), then the soonest to start. The same debut announced on two pages
 * counts once, on whichever entry sorts first.
 */
export function shinyDebuts<E extends ShinyHuntEventInput>(
  events: readonly E[],
  now: number = Date.now(),
): ShinyDebut<E>[] {
  const candidates: ShinyDebut<E>[] = [];
  const seen = new Set<string>();
  for (const ev of events) {
    if (seen.has(ev.id)) continue;
    seen.add(ev.id);
    const debuts = (ev.shinyFeatures ?? []).filter((f) => f.kind === "debut" && validFeature(f));
    if (debuts.length === 0) continue;
    const start = eventStartMs(ev);
    const end = eventEndMs(ev);
    if (!Number.isFinite(start) || !Number.isFinite(end)) continue;
    if (now >= end + SHINY_DEBUT_LINGER_MS) continue;
    if (now < start && start - now > SHINY_DEBUT_PREVIEW_MS) continue;
    const targets = dedupeTargets(
      debuts.map((f) => ({ speciesId: f.speciesId, ...(f.formSuffix ? { formSuffix: f.formSuffix } : {}) })),
    );
    candidates.push({
      event: ev,
      targets,
      live: now >= start && now < end,
      upcoming: now < start,
      startMs: start,
      endMs: end,
    });
  }
  candidates.sort(
    (a, b) =>
      Number(b.live) - Number(a.live) ||
      (a.live && b.live ? a.endMs - b.endMs : a.startMs - b.startMs),
  );
  const seenTargets = new Set<string>();
  const out: ShinyDebut<E>[] = [];
  for (const c of candidates) {
    const targets = c.targets.filter((t) => {
      const k = targetKey(t);
      if (seenTargets.has(k)) return false;
      seenTargets.add(k);
      return true;
    });
    if (targets.length > 0) out.push({ ...c, targets });
  }
  return out;
}
